import React, { useMemo, useState, useCallback, useEffect, FC } from 'react';
import {
  ColumnProps,
  H4,
  Toggle,
  Table,
  styled,
  Flex,
  FlexItem,
  Spinner,
  H3,
} from '@erecruit/ui-kit';
import { getNotificationService, NotificationSetting } from '@erecruit/modules';
import { Intl } from '@erecruit/core';
import { generateNotificationTypes, NotificationTypeEntry } from './utilities';

const NotificationSettingsContainer = styled(Flex)({
  width: '70%',
});

const NotificationSettings: FC<Intl.InjectedIntlProps> = ({ intl }) => {
  const [error, setError] = useState('');
  const [isLoadingNotifications, setIsLoadingSettings] = useState(true);
  const [enabledNotifications, setLocalEnabledNotifications] = useState<
    NotificationSetting[]
  >([]);

  const isError = error !== '';

  const notificationTypes = useMemo(() => {
    return generateNotificationTypes(enabledNotifications, intl);
  }, [enabledNotifications, intl]);

  useEffect(() => {
    if (!isLoadingNotifications) {
      getNotificationService().setEnabledNotifications(enabledNotifications);
    }
  }, [enabledNotifications, isLoadingNotifications]);

  useEffect(() => {
    const getNotifications = async () => {
      const response = await getNotificationService().getEnabledNotifications();
      const responseData = response[0].data;
      const enabledNotifications = responseData.map(x => ({
        notificationType: x.notificationType,
        enabled: x.enabled,
      }));

      if (enabledNotifications === undefined) {
        setError(
          intl.formatMessage({
            id: messageIds.vms.settings.notifications.error,
          }),
        );
      } else {
        setLocalEnabledNotifications(enabledNotifications);
      }

      setIsLoadingSettings(false);
    };

    getNotifications();
  }, [intl]);

  const updateNotificationSettings = useCallback(
    (checked: boolean, notificationId: number) => {
      const notificationSetting = enabledNotifications.find(
        x => x.notificationType === notificationId,
      );

      if (notificationSetting) {
        notificationSetting.enabled = checked;
      }

      setLocalEnabledNotifications([...enabledNotifications]);
    },
    [enabledNotifications],
  );

  const columns: ColumnProps<NotificationTypeEntry>[] = useMemo(
    () => [
      {
        key: 'name',
        dataIndex: 'name',
        title: intl.formatMessage({
          id: messageIds.vms.settings.notifications.nameColumn,
        }),
        width: '75%',
        render: (name: string) => <H4>{name}</H4>,
      },
      {
        key: 'enabled',
        dataIndex: 'enabled',
        title: intl.formatMessage({
          id: messageIds.vms.settings.notifications.statusColumn,
        }),
        width: '25%',
        render: (value: boolean, { notificationId }: NotificationTypeEntry) => {
          return (
            <Flex align="center" justify="right">
              <Toggle
                checked={value}
                onChange={checked =>
                  updateNotificationSettings(checked, notificationId)
                }
              />
            </Flex>
          );
        },
      },
    ],
    [updateNotificationSettings, intl],
  );

  const content = isError ? (
    <H3 color="critical">{error}</H3>
  ) : (
    <Table
      columns={columns}
      dataSource={notificationTypes}
      alwaysVisibleColumns={['name', 'status']}
      pagination={false}
      columnSelector={false}
    />
  );

  return (
    <NotificationSettingsContainer>
      <FlexItem>
        {isLoadingNotifications ? (
          <Spinner
            size="large"
            tip={intl.formatMessage({
              id: messageIds.vms.settings.notifications.initializing,
            })}
          />
        ) : (
          content
        )}
      </FlexItem>
    </NotificationSettingsContainer>
  );
};

const NotificationSettingsIntl = Intl.injectIntl(NotificationSettings);
export default NotificationSettingsIntl;
